import { useNetwork } from "@eusoft/webapp-framework/helpers/Network";
import { HttpError } from "@eusoft/webapp-framework/services/BaseApiClient";
import { MessageBoxButton, userInteraction } from "@eusoft/webapp-framework/services/UserInteraction";
import { formatText } from "@eusoft/webapp-ui/utils/Format";
import { apiClient } from "./PmApiClient";
import type { CommandMap } from "../entities/Commands";
import type { IApiError } from "../entities/Types";

interface ICacheItem<TItem = unknown> {
    value: TItem;
    expiresAt: Date;
}

const cache = new Map<string, ICacheItem>(); 


export class ApiError extends Error {
    constructor(error?: IApiError[], options?: ErrorOptions) {
        super(error.map(a => formatText(a.message)).join("\n"), options);
        this.error = error;
    }

    has(msg: string) {

        return this.error?.some(a => a.message == msg);  
    }

    readonly error: IApiError[];
}

export async function executeCacheAsync<
    TCommand extends keyof CommandMap>(command: TCommand, data?: Partial<CommandMap[TCommand]["command"]>, timeoutSec = 60) {

    let key = command as string;
    if (data)
        key += "@" + JSON.stringify(data);

    let item = cache.get(key);    
    const now = new Date();

    if (!item || item.expiresAt <= new Date()) {
        item ??= { } as ICacheItem;
        item.value = await useNetwork(() => apiClient.executeAsync(command, data));
        item.expiresAt = new Date(now.getTime() + timeoutSec * 1000);   
        cache.set(key, item);   
    }

    return item.value as CommandMap[TCommand]["result"];
}

export interface IExecuteOptions {
    method?: "GET" | "POST";    
    onError?: (error: Error, code?: string) => boolean;
    canRetry?: boolean;
    errorMode?: "full" | "toast";
}

export async function executeAsync<
    TCommand extends keyof CommandMap>(
        command: TCommand,
        data?: Partial<CommandMap[TCommand]["command"]>,
        options?: IExecuteOptions) {

    while (true) {
        try {
            return await useNetwork(() => apiClient.executeAsync(command, data, options?.method));
        }
        catch (ex) {

            console.warn(ex);

            let code: string;

            if (ex instanceof HttpError) {

                try {
                    const json = await ex.response.text();

                    let apiErrors = JSON.parse(json) as IApiError | IApiError[];

                    if (!Array.isArray(apiErrors))
                        apiErrors = [apiErrors];

                    ex = new ApiError(apiErrors);

                    code = apiErrors[0].code ?? apiErrors[0].message;

                    console.warn(ex);
                }
                catch {

                }
            }

            if (options?.onError && options.onError(ex, code)) {
                return;
            }

            let canRetry = true;
            let message = ex?.message;


            if (ex instanceof HttpError) {

                if (ex.response.status == 500)
                    message = "err-service-unavailable";
            }

            else if (ex instanceof ApiError) {

                canRetry = false;
            }
            else
                message = "err-server-unknown";

            if (!canRetry || options?.canRetry === false) {
                if (options?.errorMode == "full") {

                  await userInteraction.fullMessageAsync(
                        formatText(message),
                        "error",
                        [MessageBoxButton.Close]);
                }
                else {
                    userInteraction.messageAsync(
                        formatText(message),
                        "error",
                        4000);
                }
                return;
            }

            const res = await userInteraction.messageBoxAsync(
                formatText(message),
                "error",
                MessageBoxButton.RetryCancel);

            if (res === MessageBoxButton.Cancel)
                return;
        }
    }
}

export function invalidateCache<TCommand extends keyof CommandMap>(command: TCommand) {

    for (const key of cache.keys()) {

        if (key.startsWith(command as string))
            cache.delete(key);
    }
}